import { usePostHog } from "@posthog/react";
import { ColumnDef } from "@tanstack/react-table";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { Button } from "components/ui/button";
import { Dataset, DatasetList } from "data/models";
import { fetchDatasetsLink } from "data/queries-common";
import { CircleCheck, CircleX, Download } from "lucide-react";
import { formatProductName } from "pages/geoscape-admin/manage-release/new-release/utils";
import { formatDate } from "utils";
import { formatBytes } from "utils/commons";

type Dictionary = {
  [key: string]: string;
};

const DisplayFormatMapping: Dictionary = {
  GDB: "GDB",
  GEOJSON: "GeoJSON",
  SHP: "Shapefile",
  TAB: "MapInfo TAB",
  LYNX: "LYNX",
  PUMP: "PUMP",
  GEOTIFF: "GEOTIFF",
  PSV: "Pipe-Separated Values",
};

const DisplayStateMapping: Dictionary = {
  "ALL STATES": "All States",
  AUSTRALIA: "Australia",
};

type DatasetRow = {
  id: string;
  name: string;
  parentProduct?: string;
  product: string;
  insight: string;
  region: string;
  format: string;
  size: string | number;
  release: string;
  publishDate: string;
  licenced: boolean;
  datum: string;
};

const customColumns = (): ColumnDef<DatasetRow>[] => {
  const { enqueueLinkCorruptedSnackbar } = useCustomSnackbars();
  const posthog = usePostHog();
  return [
    {
      accessorKey: "product",
      header: "Product",
      enableSorting: true,
      cell: ({ row }) => {
        return row.original.parentProduct
          ? row.original.product
          : row.original.name;
      },
    },
    {
      accessorKey: "insight",
      header: "Insight Pack",
      enableSorting: true,
    },
    {
      accessorKey: "release",
      header: "Release",
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        const [year, month] = val.split("-").map(Number);
        const monthName = new Date(year, month - 1).toLocaleString("default", {
          month: "long",
        });
        return `${monthName} ${year}`;
      },
    },
    {
      accessorKey: "region",
      header: "Region",
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        return DisplayStateMapping[val] ?? val;
      },
    },
    {
      accessorKey: "format",
      header: "Format",
      enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        return DisplayFormatMapping[val] ?? val;
      },
    },
    {
      accessorKey: "datum",
      header: "Datum",
      enableSorting: true,
    },
    {
      accessorKey: "size",
      header: "File Size",
      enableSorting: true,
      cell: ({ getValue }) => formatBytes(getValue() as number),
    },
    {
      accessorKey: "publishDate",
      header: "Publish Date",
      enableSorting: true,
      cell: ({ getValue }) => formatDate(getValue() as string),
    },
    {
      accessorKey: "licenced",
      header: "Licenced",
      enableSorting: true,
      cell: ({ getValue }) =>
        getValue() ? (
          <div className="flex w-full h-full items-center justify-center">
            <CircleCheck className="text-success" />
          </div>
        ) : (
          <div className="flex w-full h-full items-center justify-center">
            <CircleX className="text-destructive" />
          </div>
        ),
    },
    {
      accessorKey: "id",
      header: "",
      cell: ({ row }) => {
        return (
          <div className="flex items-center justify-center w-full h-full">
            {row.original.licenced ? (
              <Button
                variant="outline"
                size="icon-lg"
                onClick={async () => {
                  try {
                    const response = await fetchDatasetsLink(row.original.id);

                    const a = document.createElement("a");
                    a.href = response;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(response);
                    posthog.capture("user_downloaded_dataset", {
                      id: row.original.id,
                      name: row.original.name,
                      region: row.original.region,
                      release: row.original.release,
                      datum: row.original.datum,
                      format: row.original.format,
                    });
                  } catch (er) {
                    enqueueLinkCorruptedSnackbar();
                  }
                }}
              >
                <Download className="text-primary" />
              </Button>
            ) : (
              <Button variant="ghost" size="icon-lg" disabled>
                <Download className="text-muted-foreground" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];
};

const customersToRows = (datasetsList: DatasetList): DatasetRow[] => {
  const rows = datasetsList.datasets.map((key: Dataset) => {
    return {
      id: key.id,
      name: key.name,
      parentProduct: key.parentProduct,
      product: !!key.parentProduct
        ? formatProductName(key.parentProduct)
        : formatProductName(key.product),
      insight: key.parentProduct ? formatProductName(key.product) : "-",
      region: key.region,
      format: key.format,
      size: key.size,
      release: key.release,
      publishDate: key.publishDate,
      licenced: key.licenced,
      datum: key.datum,
    };
  });
  return rows;
};

export { customColumns, customersToRows };
export type { DatasetRow };
